import React, { useState, useEffect, useRef } from "react"
import { QRCodeCanvas } from "qrcode.react"
import InviteSmart from "@/assets/images/inviteSmart.png"
import InvitesmartLogonew from "@/assets/images/invitesmartLogoneww.png"

import Skeleton from "@/components/common/skeleton"
import QRGenerator from "@/pages/addInvite/generateQrCode"
import { RgbToHex } from "@/pages/addInvite/displayColor"
import html2canvas from "html2canvas"
import { IoChevronBack } from "react-icons/io5"
import { IoMdCheckmark } from "react-icons/io"
import { useTranslation } from "react-i18next"
import Spinner from "react-bootstrap/Spinner"
import { IoMdShare } from "react-icons/io"
import NameLabel from "@/pages/addInvite/nameLabel"
// Font styles array
const fonts = [
  {
    name: "NotoSans_regular",
    label: "Noto Sans",
    fontFamily: "'Noto Sans', sans-serif",
  },
  { name: "Rubik_regular", label: "Rubik", fontFamily: "'Rubik', sans-serif" },
  { name: "Amiri_regular", label: "Amiri", fontFamily: "'Amiri', serif" },
  { name: "Cairo_regular", label: "Cairo", fontFamily: "'Cairo', sans-serif" },
  {
    name: "ScheherazadeNew_regular",
    label: "Scheherazade",
    fontFamily: "'Scheherazade', serif",
  },
  {
    name: "MarkaziText_regular",
    label: "Markazi",
    fontFamily: "'Markazi Text', serif",
  },

  { name: "Lora_regular", label: "Lora", fontFamily: "'Lora', serif" },
  {
    name: "Montserrat_regular",
    label: "Montserrat",
    fontFamily: "'Montserrat', sans-serif",
  },
]
const getFontFamily = (fontName: string) => {
  const font = fonts.find((font: any) => font?.name === fontName)
  return font?.fontFamily || "default-font, sans-serif" // Fallback font
}

export const hexToRgb = (hex: string): string => {
  // Remove the hash if present
  const cleanedHex = hex.replace("#", "")

  // Extract RGB components
  const bigint = parseInt(cleanedHex, 16)
  const r = (bigint >> 16) & 255
  const g = (bigint >> 8) & 255
  const b = bigint & 255

  return `rgb(${r}, ${g}, ${b})`
}

const normalizePosition = (
  value: number,
  maxValue: number,
  defaultLower: number,
  defaultUpper: number,
) => {
  if (value < 0) return defaultLower * maxValue // Below bounds
  if (value > 1) return defaultUpper * maxValue // Above bounds
  return value * maxValue // Within bounds
}
function constrainPosition(
  position: number,
  containerSize: number,
  elementSize: number,
) {
  const min = elementSize / 2
  const max = containerSize - elementSize / 2
  return Math.min(Math.max(position, min), max)
}
const ResponsiveImageWithQRCode = ({
  currentContact,
  event,
  qrCodeData,
  setDisplayImage,
}: {
  currentContact?: any
  event?: any
  qrCodeData?: any
  setDisplayImage: (value: any) => void
}) => {
  const { t } = useTranslation()
  const [loading, setLoading] = useState(false)
  const [isLoadingImage, setIsLoadingImage] = useState(true)
  const uploadedImageRef = useRef<HTMLDivElement | null>(null)
  const containerRef = useRef<HTMLDivElement | null>(null)

  ////references
  const qrDivRef = useRef<HTMLDivElement | null>(null)
  const nameDivRef = useRef<HTMLDivElement | null>(null)
  const logoDivRef = useRef<HTMLImageElement | null>(null)
  const imageRef = useRef<HTMLImageElement | null>(null)

  ////qrCode managing
  const [qrPosition, setQrPosition] = useState({ x: 5, y: 2 })
  const [qrSize, setQrSize] = useState(50)
  ///name managing
  const [namePosition, setNamePosition] = useState({ x: 5, y: 2 })
  const [nameSize, setNameSize] = useState(16)
  ////logo managing
  const [logoPosition, setLogoPosition] = useState({ x: 100, y: 2 })

  const prepareImageForShare = async () => {
    setLoading(true)
    try {
      if (containerRef.current) {
        const canvas = await html2canvas(containerRef.current, {
          useCORS: true,
          backgroundColor: null,
        })

        // Trigger image download first
        canvas.toBlob(async (blob) => {
          if (blob) {
            console.log("blob", blob)
            const imageUrl = URL.createObjectURL(blob)
            const response = await fetch(imageUrl)
            console.log("response", response)
            const blobN = await response.blob()
            const file = new File([blobN], "invite.jpg", {
              type: blobN.type,
            })

            await navigator.share({
              files: [file],
              title: "Hello , I would like to invite you to join this event",
              text: "Hello , I would like to invite you to join this event",
            })
          }
        })
      }
    } catch (err) {
      setLoading(false)
    } finally {
      setLoading(false)
    }
  }
  useEffect(() => {
    if (
      containerRef.current &&
      qrCodeData &&
      qrDivRef.current &&
      logoDivRef.current &&
      imageRef.current &&
      nameDivRef.current
    ) {
      const {
        x_qr,
        y_qr,
        logo_position_x,
        logo_position_y,
        qr_size,
        name_position_x,
        name_position_y,
        name_size,
      } = qrCodeData
      const imageWidth = imageRef.current.offsetWidth
      const imageHeight = imageRef.current.offsetHeight
      const qrCodeWidth = qrDivRef.current.offsetWidth
      const qrCodeHeight = qrDivRef.current.offsetHeight
      const nameWidth = nameDivRef.current.offsetWidth
      const nameHeight = nameDivRef.current.offsetHeight
      const logoWidth = logoDivRef.current.offsetWidth
      const logoHeight = logoDivRef.current.offsetHeight
      // Normalize QR code position and constrain within image boundaries
      setQrPosition({
        x: constrainPosition(
          normalizePosition(x_qr ?? 0, imageWidth, 0.1, 0.9),
          imageWidth,
          qrCodeWidth,
        ),
        y: constrainPosition(
          normalizePosition(y_qr ?? 0, imageHeight, 0.1, 0.9),
          imageHeight,
          qrCodeHeight,
        ),
      })
      setQrSize(qr_size * imageWidth)

      /////normalize name
      /////normalize name
      // Check if name_position_x, name_position_y, and name_size are not null before setting the name position and size
      if (
        name_position_x !== null &&
        name_position_y !== null &&
        name_size !== null
      ) {
        const nameWidth = nameDivRef.current.offsetWidth
        const nameHeight = nameDivRef.current.offsetHeight

        setNamePosition({
          x: constrainPosition(
            normalizePosition(name_position_x, imageWidth, 0.1, 0.9),
            imageWidth,
            nameWidth,
          ),
          y: constrainPosition(
            normalizePosition(name_position_y, imageHeight, 0.1, 0.9),
            imageHeight,
            nameHeight,
          ),
        })
        setNameSize(name_size * imageWidth)
      }
      // Normalize Logo position (assuming similar constraints for the logo)
      setLogoPosition({
        x: constrainPosition(
          normalizePosition(logo_position_x ?? 0, imageWidth, 0.1, 0.9),
          imageWidth,
          logoWidth, // Adjust if logo size differs
        ),
        y: constrainPosition(
          normalizePosition(logo_position_y ?? 0, imageHeight, 0.1, 0.9),
          imageHeight,
          logoHeight, // Adjust if logo size differs
        ),
      })

      // setDefaultQrSize(qrCodeData?.data?.data?.qr_size * imageWidth);
    }

    // Helper function to constrain position
    function constrainPosition(
      position: number,
      containerSize: number,
      elementSize: number,
    ) {
      const min = 0 // Start of the container
      const max = containerSize - elementSize // End of the container minus the element's size
      return Math.min(Math.max(position, min), max)
    }
  }, [containerRef.current, qrCodeData, qrDivRef.current])

  return (
    <>
      <div
        className="ps-3 pe-3"
        style={{
          cursor: "pointer",
          display: "flex",

          justifyContent: "space-between",
        }}
      >
        <div>
          <IoChevronBack
            onClick={() => {
              setDisplayImage(false)
            }}
            style={{ fontSize: "25px" }}
          />
        </div>

        {currentContact?.status == 4 && (
          <h6 className="d-flex gap-2 align-items-center">
            {" "}
            <IoMdCheckmark style={{ fontSize: "19px" }} />{" "}
            <span style={{ fontSize: "14px" }}> {t("invitation.scanned")}</span>
          </h6>
        )}
        <div
          style={{
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {" "}
          {loading ? (
            <div
              className="d-flex justify-content-center align-items-center"
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <Spinner animation="border" role="status">
                <span className="visually-hidden">Loading...</span>
              </Spinner>
            </div>
          ) : (
            <IoMdShare
              onClick={prepareImageForShare}
              style={{ fontSize: "25px", color: "#245192" }}
            />
            // </button>
          )}
        </div>
      </div>
      <hr />
      {[1, "1"].includes(qrCodeData?.outside_image) ? (
        <div
          ref={containerRef}
          style={{
            position: "relative",
            width: "300px",
            // border: "1px solid #ccc",
            padding: "5px",
            backgroundColor: "#fff",
          }}
        >
          {/* Image section */}
          <div
            style={{
              width: "300px",
              height: "300px", // Adjust height to accommodate the image
            }}
          >
            <img
              src={qrCodeData?.image_url}
              alt="Main Background"
              style={{
                width: "100%",
                height: "100%",
                // objectFit: "cover",
                // objectPosition: "center",
              }}
            />
          </div>

          {/* QR Code and Logo Section */}
          <div
            style={{
              display: "flex",
              flexDirection: "row",
              alignItems: "flex-end", // Align items to the bottom
              justifyContent: "space-between",
            }}
          >
            {/* QR Code */}
            <div
              className="d-flex  flex-row gap-2 "
              style={{
                alignItems: "flex-end", // Align items to the bottom
              }}
            >
              <QRGenerator
                value={`https://mazoom.app/check-qrcode/${event?.event_id}/${currentContact?.hash}`}
                bgColor={
                  qrCodeData?.qr_color_code
                    ? RgbToHex(hexToRgb(qrCodeData?.qr_color_code))
                    : "#000000"
                }
                size={70}
              />
              <div className="mb-2">
                <NameLabel
                  label={currentContact?.first_name}
                  color={RgbToHex(qrCodeData?.name_color || "#000000")}
                  size={nameSize}
                  font={getFontFamily(qrCodeData?.font_type)}
                />
              </div>
            </div>

            {/* Name or Number (Dynamic) */}

            {/* Logo */}
            {!qrCodeData?.remove_logo && (
              <img
                src={InvitesmartLogonew}
                alt="Logo"
                style={{
                  marginBottom: "5px",
                  marginTop: "10px",
                  width: "60px",
                  height: "60px",
                  objectFit: "cover",
                  objectPosition: "center",
                }}
              />
            )}
          </div>
        </div>
      ) : (
        <div
          ref={containerRef}
          style={{
            position: "relative",
            // width: "100%",
            // height: "100%",
            width: "300px",
            height: "300px",
            // border: "1px solid #ccc",
            marginBottom: "20px",
            overflow: "hidden", // Ensures content outside container doesn't appear unless needed
          }}
        >
          {/* Main Image */}
          <img
            ref={imageRef}
            src={qrCodeData?.image_url}
            alt="Main Background"
            style={{
              width: "100%",
              height: "100%",
              position: "absolute",
              // objectFit: "cover",
              // objectPosition: "center",
              top: 0,
              left: 0,
            }}
          />

          {/* QR Code */}
          <div
            className="d-flex  flex-column gap-0 align-items-center"
            ref={qrDivRef}
            style={{
              position: "absolute",
              // left: `${qrX * 280}px`,
              // top: `${qrY * 300}px`,
              left: `${qrPosition?.x}px`,
              top: `${qrPosition?.y}px`,
            }}
          >
            <QRGenerator
              value={`https://mazoom.app/check-qrcode/${event?.event_id}/${currentContact?.hash}`}
              bgColor={
                qrCodeData?.qr_color_code
                  ? RgbToHex(hexToRgb(qrCodeData?.qr_color_code))
                  : "#000000"
              }
              size={qrSize}
            />
          </div>

          {/* Name or Number (Dynamic) */}
          <div
            ref={nameDivRef}
            className="d-flex  flex-column gap-0 align-items-center"
            style={{
              position: "absolute",
              top: `${namePosition.y}px`,
              left: `${namePosition.x}px`,
            }}
          >
            <NameLabel
              label={currentContact?.first_name}
              color={RgbToHex(qrCodeData?.name_color || "#000000")}
              size={nameSize}
              font={getFontFamily(qrCodeData?.font_type)}
            />
          </div>
          {/* Logo */}
          {!qrCodeData?.remove_logo && (
            <img
              ref={logoDivRef}
              src={InvitesmartLogonew}
              alt="Logo"
              style={{
                position: "absolute",
                // left: `${logoX * 400}px`,
                // top: `${logoY * 300}px`,

                // left: `${logoX < 1 ? logoX * 400 : 0.8 * 400}px`,
                // top: `${logoY < 1 ? logoY * 300 : 0.8 * 300}px`,
                left: `${logoPosition?.x}px`,
                top: `${logoPosition?.y}px`,

                width: "40px",
                height: "50px",
                // objectFit: "cover",
                // objectPosition: "center",
              }}
            />
          )}
        </div>
      )}
    </>
  )
}

export default ResponsiveImageWithQRCode
